import { useContext, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";

import { DrawingContext, DrawingMode, DrawingFeatureType } from "../contexts/DrawingContext";
import { useProject } from "../api/project";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { feetToMeters } from "../utils/units";
import { useApplyVegetationEdit } from "../api/vegetationEdits";
import FilestoreBrowser from "./FilestoreBrowser";
import WidthSelectionModal, { WidthConfig } from "./WidthSelectionModal";
import { parseFilestoreFile, FilestoreFile } from "../api/filestore";
import {
  applyWidthConfig,
  collectPropertyFields,
  hasGeometryOfType,
  LINE_GEOMETRY_TYPES,
  POINT_GEOMETRY_TYPES,
} from "../utils/geojsonUtils";

const EditVegetationModal = () => {
  const unitSystem = useUnitSystem();
  const { project } = useProject();
  const { mutateAsync: applyEdit, isPending } = useApplyVegetationEdit();

  const {
    drawingMode,
    setDrawingMode,
    drawingFeatureType,
    setDrawingFeatureType,
    geojson,
  } = useContext(DrawingContext);

  const [open, setOpen] = useState(false);
  const [vegClass, setVegClass] = useState<string>("");
  const [width, setWidth] = useState<number>(10);
  const [errors, setErrors] = useState<{ vegClass?: string; width?: string; geometry?: string }>({});

  // File upload state
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [uploadedGeojson, setUploadedGeojson] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<FilestoreFile | null>(null);
  const [widthSelectionOpen, setWidthSelectionOpen] = useState(false);

  const isUploadFlow = drawingFeatureType === DrawingFeatureType.UPLOAD;
  const uploadHasLines = useMemo(
    () => isUploadFlow && hasGeometryOfType(uploadedGeojson, LINE_GEOMETRY_TYPES),
    [isUploadFlow, uploadedGeojson]
  );
  const uploadFields = useMemo(() => collectPropertyFields(uploadedGeojson), [uploadedGeojson]);

  const availableVegClasses = useMemo(() => {
    const classes = project?.data?.veg_classes;
    return Array.isArray(classes) ? classes.map((v) => String(v)) : [];
  }, [project?.data?.veg_classes]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!vegClass && availableVegClasses.length > 0) {
      setVegClass(availableVegClasses[0]);
    }
  }, [availableVegClasses, open, vegClass]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && drawingMode === DrawingMode.VEGETATION_EDIT) {
      setOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawingMode]);

  // Auto-open modal when upload feature type is selected
  useEffect(() => {
    if (
      drawingMode === DrawingMode.VEGETATION_EDIT &&
      drawingFeatureType === DrawingFeatureType.UPLOAD
    ) {
      setOpen(true);
    }
  }, [drawingMode, drawingFeatureType]);

  const reset = () => {
    setOpen(false);
    setErrors({});
    setVegClass("");
    setWidth(10);
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
    setFileName("");
    setFileError("");
    setUploadedGeojson(null);
    setSelectedFile(null);
    setWidthSelectionOpen(false);
    setIsFileLoading(false);
  };

  const handleFilestoreFileSelected = async (filestoreFile: FilestoreFile) => {
    setFileError("");
    setIsFileLoading(true);
    try {
      const parsed = await parseFilestoreFile(filestoreFile.id);

      // Validate that we got a proper GeoJSON FeatureCollection with features
      if (!parsed || typeof parsed !== 'object') {
        throw new Error("Invalid file format: not a valid GeoJSON object");
      }

      // Handle both direct GeoJSON and wrapped response formats
      const geojsonData = parsed.features ? parsed : parsed.data;

      if (!geojsonData || !Array.isArray(geojsonData.features)) {
        throw new Error("Invalid file format: missing features array");
      }

      if (geojsonData.features.length === 0) {
        throw new Error("File contains no features");
      }

      // Check that at least one feature has geometry
      const hasValidGeometry = geojsonData.features.some((f: any) => f?.geometry);
      if (!hasValidGeometry) {
        throw new Error("File contains no valid geometries");
      }

      if (hasGeometryOfType(geojsonData, POINT_GEOMETRY_TYPES)) {
        throw new Error(
          "Point features are not supported for vegetation edits. Remove them from the file, or upload them as assets instead."
        );
      }

      setUploadedGeojson(geojsonData);
      setFileName(filestoreFile.original_filename);
      setFileError("");

      // Line features need a width so the backend can buffer them to polygons
      if (hasGeometryOfType(geojsonData, LINE_GEOMETRY_TYPES)) {
        setSelectedFile(filestoreFile);
        setWidthSelectionOpen(true);
      }
    } catch (err: any) {
      console.error(err);
      setFileError(err?.message || "Failed to load file from filestore");
      setUploadedGeojson(null);
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleWidthConfigApply = (config: WidthConfig) => {
    setUploadedGeojson((prev: any) =>
      prev ? applyWidthConfig(prev, config, LINE_GEOMETRY_TYPES) : prev
    );
    setErrors((prev) => ({ ...prev, width: undefined }));
  };

  const validate = () => {
    const next: { vegClass?: string; width?: string; geometry?: string } = {};

    if (!vegClass) {
      next.vegClass = "Vegetation type is required";
    }

    // Use uploaded geojson for upload flow, otherwise use drawn geojson
    const sourceGeojson = isUploadFlow ? uploadedGeojson : geojson;
    const hasGeometry = sourceGeojson?.features?.some((f: any) => f?.geometry);
    if (!hasGeometry) {
      next.geometry = isUploadFlow ? "No file loaded" : "No geometry drawn";
    }

    if (isUploadFlow) {
      // Every uploaded line feature must carry a positive width property
      // (stamped by the width dialog) so the backend can buffer it.
      const missingWidth = uploadedGeojson?.features?.some(
        (f: any) =>
          LINE_GEOMETRY_TYPES.includes(f?.geometry?.type) &&
          !(Number.isFinite(Number(f?.properties?.width)) && Number(f?.properties?.width) > 0)
      );
      if (missingWidth) {
        next.width = "Line features need a width — configure it before applying";
      }
    } else if (drawingFeatureType === DrawingFeatureType.LINE) {
      if (!Number.isFinite(width) || width <= 0) {
        next.width = "Width should be a positive number";
      }
    }

    return next;
  };

  const handleSubmit = async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      // Uploaded lines are missing widths — reopen the width dialog
      if (nextErrors.width && isUploadFlow && uploadHasLines) {
        setWidthSelectionOpen(true);
      }
      return;
    }

    // Use uploaded geojson for upload flow, otherwise use drawn geojson.
    // Uploaded line features carry their own per-feature width property;
    // only drawn lines send a payload-level width.
    const sourceGeojson = isUploadFlow ? uploadedGeojson : geojson;

    const metricWidth =
      !isUploadFlow && drawingFeatureType === DrawingFeatureType.LINE
        ? unitSystem === "imperial"
          ? feetToMeters(Number(width))
          : Number(width)
        : undefined;

    try {
      await applyEdit({
        vegClass,
        geojson: sourceGeojson,
        ...(metricWidth ? { width: metricWidth } : {}),
      });
      reset();
    } catch (e) {
      console.error("Vegetation edit failed", e);
      setErrors({ geometry: "Failed to apply vegetation edit. Please try again." });
    }
  };

  if (!open) return null;

  const widthSuffix = unitSystem === "imperial" ? "ft" : "m";

  return (
    <Modal
      open={open}
      onClose={reset}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          p: 3,
          minWidth: 340,
          maxWidth: 440,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Edit Vegetation ({drawingFeatureType})
        </Typography>

        {/* Upload Section */}
        {isUploadFlow && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Shapefiles must be uploaded as a .zip archive.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setFilestoreBrowserOpen(true)}
                disabled={isFileLoading}
              >
                Browse My Files
              </Button>
              {fileName && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {fileName}
                </Typography>
              )}
            </Box>

            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}

            {isFileLoading && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading file...
                </Typography>
              </Box>
            )}

            <FilestoreBrowser
              open={filestoreBrowserOpen}
              onClose={() => setFilestoreBrowserOpen(false)}
              onSelectFile={handleFilestoreFileSelected}
              acceptedFileTypes=".zip,.kml,.geojson,.json"
            />

            {selectedFile && (
              <WidthSelectionModal
                open={widthSelectionOpen}
                onClose={() => setWidthSelectionOpen(false)}
                onApply={handleWidthConfigApply}
                file={selectedFile}
                fields={uploadFields}
                geojson={uploadedGeojson}
              />
            )}

            {uploadHasLines && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Line features will be buffered to polygons using the configured width.
                </Typography>
                <Button size="small" onClick={() => setWidthSelectionOpen(true)}>
                  Change widths
                </Button>
              </Box>
            )}
          </Box>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.vegClass}>
          <InputLabel id="veg-class-label">Vegetation Type</InputLabel>
          <Select
            labelId="veg-class-label"
            label="Vegetation Type"
            value={vegClass}
            onChange={(e) => setVegClass(e.target.value as string)}
          >
            {availableVegClasses.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
          {errors.vegClass ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.vegClass}
            </Typography>
          ) : null}
        </FormControl>

        {/* Width field for drawn lines; uploaded line widths come from the width dialog */}
        {drawingFeatureType === DrawingFeatureType.LINE ? (
          <TextField
            fullWidth
            label={`Width (${widthSuffix})`}
            value={width}
            onChange={(e) => setWidth(Number(e.target.value))}
            InputProps={{
              type: "number",
              endAdornment: <InputAdornment position="end">{widthSuffix}</InputAdornment>,
              inputProps: { min: 0, step: "any" },
            }}
            error={!!errors.width}
            helperText={errors.width}
            sx={{ mb: 2 }}
          />
        ) : null}

        {isUploadFlow && errors.width ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {errors.width}
          </Typography>
        ) : null}

        {errors.geometry ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {errors.geometry}
          </Typography>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={reset} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isPending}>
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditVegetationModal;
